import {
  ChannelIcon,
  CircleCheckBigIcon,
  Flex,
  Text,
} from '@buffer-mono/popcorn'
import type { Service } from '~publish/pages/Community/graphqlEnums'
import { isChannelIconService } from '~publish/pages/Community/util/isChannelIconService'

import { useCommentCountsForPost } from '../../context/CommentsByPostCountsContext'
import type { FilterStatus } from '../../../CommunityFilters/types'
import { getDisplayServiceName } from '../../../util/channelHelpers'
import { RETENTION_COUNT_SUFFIX } from '../../../util/retention'
import { formatCompactCount } from '../CommentMetrics/commentMetricsUtils'

import styles from './CommentsSummaryBar.module.css'

import type { JSX } from 'react'

function getAllRepliedText(total: number, isGBP: boolean): string {
  const itemLabel = isGBP ? 'review' : 'comment'
  if (total === 1) {
    return `You've replied to the 1 ${itemLabel} on this post`
  }
  return `You've replied to all ${formatCompactCount(
    total,
  )} ${itemLabel}s for this post`
}

/**
 * Label for the total count, e.g. "3 total comments". Drops the "total"
 * qualifier and pluralization for a single item ("1 comment"). Posts older
 * than the retention window gain a suffix naming it, since only that window's
 * comments are shown.
 */
function getTotalCountLabel(
  total: number,
  totalFormatted: string,
  isGBP: boolean,
  isOlderThanRetentionWindow: boolean,
): string {
  const itemLabel = isGBP ? 'review' : 'comment'
  const suffix = isOlderThanRetentionWindow ? ` ${RETENTION_COUNT_SUFFIX}` : ''
  if (total === 1) {
    return `1 ${itemLabel}${suffix}`
  }
  return `${totalFormatted} total ${itemLabel}s${suffix}`
}

type CommentsSummaryBarProps = {
  postId: string
  channelService: Service
  status?: FilterStatus
  isOlderThanRetentionWindow?: boolean
}

export function CommentsSummaryBar({
  postId,
  channelService,
  status,
  isOlderThanRetentionWindow = false,
}: CommentsSummaryBarProps): JSX.Element | null {
  const { counts, loading } = useCommentCountsForPost(postId)

  if (loading || !counts) {
    return null
  }

  const total = counts.unreplied + counts.replied + counts.dismissed
  const addressed = counts.replied + counts.dismissed

  if (total === 0) {
    return null
  }

  const isAllReplied = counts.unreplied === 0

  // On the Unanswered filter, DefaultEmptyState renders below with a
  // "Congratulations / You're all caught up" message — the summary bar's
  // "You've replied to all comments on this post" duplicates that.
  if (status === 'unreplied' && isAllReplied) {
    return null
  }
  const totalFormatted = formatCompactCount(total)
  const isGBP = channelService === 'googlebusiness'
  const allRepliedText = getAllRepliedText(total, isGBP)
  const totalCountLabel = getTotalCountLabel(
    total,
    totalFormatted,
    isGBP,
    isOlderThanRetentionWindow,
  )

  return (
    <div className={styles.wrapper}>
      <Flex
        align="center"
        justify="between"
        wrap="wrap"
        gap="xs"
        className={styles.container}
        aria-label={
          isAllReplied
            ? allRepliedText
            : `${formatCompactCount(addressed)} replied out of ${totalCountLabel}`
        }
      >
        {isAllReplied ? (
          <Text
            size="sm"
            color="default"
            icon={
              <span className={styles.checkIcon}>
                <CircleCheckBigIcon color="success" size={12} />
              </span>
            }
          >
            {allRepliedText}
          </Text>
        ) : (
          <Text size="sm" color="subtle" className={styles.counts}>
            <Text size="sm" color="default" as="span">
              {formatCompactCount(addressed)} replied
            </Text>
            {' / '}
            {totalCountLabel}
          </Text>
        )}
        <Text size="sm" color="subtle" className={styles.repliesVia}>
          Includes replies via{' '}
          {isChannelIconService(channelService) && (
            <ChannelIcon type={channelService} size="xsmall" color="subtle" />
          )}{' '}
          {getDisplayServiceName(channelService)}
        </Text>
      </Flex>
    </div>
  )
}
